import React, { useState } from 'react';
import ReactDOM from 'react-dom';

const BreaksSection = ({ breaks, setBreaks, formatTime }) => {
  // State for controlling the add/edit modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Holds the break values for the modal (for both new and editing)
  const [currentBreak, setCurrentBreak] = useState({ name: "", days: [], start: "", end: "", color: "" });
  // When editing, this holds the index of the break being edited (null if adding new)
  const [editingBreakIndex, setEditingBreakIndex] = useState(null);

  // Expanded set of color options (from the course section plus some extras)
  const colorOptions = [
    "bg-red-400",
    "bg-green-400",
    "bg-blue-400",
    "bg-yellow-400",
    "bg-purple-400",
    "bg-pink-400",
    "bg-indigo-400",
    "bg-teal-400",
    "bg-orange-400",
  ];

  // Get available colors that are not already taken by other breaks
  const getAvailableColors = () => {
    const usedColors = breaks
      .filter((_, idx) => idx !== editingBreakIndex)
      .map(bk => bk.color);
    return colorOptions.filter(color => !usedColors.includes(color));
  };

  // ColorPicker component: shows colored boxes for selection.
  const ColorPicker = ({ availableColors, currentColor, onSelect }) => {
    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {availableColors.map((color, index) => (
          <div
            key={index}
            className={`w-8 h-8 rounded cursor-pointer border-2 ${color} ${
              currentColor === color ? 'border-black' : 'border-transparent'
            }`}
            onClick={() => onSelect(color)}
          ></div>
        ))}
      </div>
    );
  };

  // Open modal for adding a new break
  const openAddModal = () => {
    setEditingBreakIndex(null);
    const available = getAvailableColors();
    const defaultColor = available[0] || "";
    setCurrentBreak({ name: "", days: [], start: "", end: "", color: defaultColor });
    setIsModalOpen(true);
  };

  // Open modal for editing an existing break
  const openEditModal = (index) => {
    setEditingBreakIndex(index);
    setCurrentBreak({ ...breaks[index] });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentBreak({ name: "", days: [], start: "", end: "", color: "" });
    setEditingBreakIndex(null);
  };

  // Save the break (add new or update existing)
  const handleSaveBreak = () => {
    if (
      !currentBreak.name ||
      !currentBreak.days.length ||
      !currentBreak.start ||
      !currentBreak.end ||
      !currentBreak.color
    ) {
      alert("Please fill in all fields for the break.");
      return;
    }
    // Validate that start time comes before end time
    const [startHour, startMin] = currentBreak.start.split(":").map(Number);
    const [endHour, endMin] = currentBreak.end.split(":").map(Number);
    if (startHour * 60 + startMin >= endHour * 60 + endMin) {
      alert("Start time must be before end time.");
      return;
    }
    if (editingBreakIndex !== null) {
      const newBreaks = [...breaks];
      newBreaks[editingBreakIndex] = { ...currentBreak, selected: true };
      setBreaks(newBreaks);
    } else {
      setBreaks([...breaks, { ...currentBreak, selected: true }]);
    }
    closeModal();
  };

  // Toggle selection (active/inactive) for all breaks
  const handleSelectAllBreaks = (checked) => {
    if (breaks.length === 0) return;
    const updated = breaks.map(bk => ({ ...bk, selected: checked }));
    setBreaks(updated);
  };

  // Toggle a single break’s selected state
  const toggleBreakSelection = (index) => {
    const updated = breaks.map((bk, i) =>
      i === index ? { ...bk, selected: !bk.selected } : bk
    );
    setBreaks(updated);
  };

  // Remove a break from the list
  const removeBreak = (index) => {
    setBreaks(breaks.filter((_, i) => i !== index));
  };

  const DAYS = [
    { code: 'Mo', label: 'Monday' },
    { code: 'Tu', label: 'Tuesday' },
    { code: 'We', label: 'Wednesday' },
    { code: 'Th', label: 'Thursday' },
    { code: 'Fr', label: 'Friday' },
  ];

  // The modal content
  const modalContent = (
    <div
      id="break-modal"
      tabIndex="-1"
      aria-hidden="true"
      className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300 ease-in-out"
    >
      <div className="relative p-4 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {editingBreakIndex !== null ? "Edit Break" : "Add Break"}
            </h3>
            <button
              type="button"
              onClick={closeModal}
              className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
            >
              <svg
                className="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
          </div>
          {/* Modal Body */}
          <div className="p-4 md:p-5">
            <div className="space-y-4">
              {/* Break Name */}
              <div>
                <label htmlFor="breakName" className="text-sm font-medium text-gray-900 dark:text-white">
                  Break Name
                </label>
                <input
                  type="text"
                  id="breakName"
                  value={currentBreak.name}
                  onChange={(e) => setCurrentBreak({ ...currentBreak, name: e.target.value })}
                  placeholder="e.g. Gym, Work"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 w-full p-2.5 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
                />
              </div>
              {/* Select Days */}
              <div>
                <label className="text-sm font-medium text-gray-900 dark:text-white">
                  Select Days
                </label>
                <div className="flex space-x-2 mt-1">
                  {DAYS.map(day => (
                    <label key={day.code} className="inline-flex items-center">
                      <input
                        type="checkbox"
                        value={day.code}
                        checked={currentBreak.days.includes(day.code)}
                        onChange={() => {
                          const updatedDays = currentBreak.days.includes(day.code)
                            ? currentBreak.days.filter(d => d !== day.code)
                            : [...currentBreak.days, day.code];
                          setCurrentBreak({ ...currentBreak, days: updatedDays });
                        }}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                      <span className="ml-1 text-sm text-gray-900 dark:text-white">
                        {day.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              {/* Start Time & End Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="startTime" className="text-sm font-medium text-gray-900 dark:text-white">
                    Start Time
                  </label>
                  <input
                    type="time"
                    id="startTime"
                    value={currentBreak.start}
                    onChange={(e) => setCurrentBreak({ ...currentBreak, start: e.target.value })}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 w-full p-2.5 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
                  />
                </div>
                <div>
                  <label htmlFor="endTime" className="text-sm font-medium text-gray-900 dark:text-white">
                    End Time
                  </label>
                  <input
                    type="time"
                    id="endTime"
                    value={currentBreak.end}
                    onChange={(e) => setCurrentBreak({ ...currentBreak, end: e.target.value })}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 w-full p-2.5 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
                  />
                </div>
              </div>
              {/* Color Selection */}
              <div>
                <label className="text-sm font-medium text-gray-900 dark:text-white">
                  Break Color
                </label>
                <ColorPicker
                  availableColors={
                    getAvailableColors().includes(currentBreak.color)
                      ? getAvailableColors()
                      : [...getAvailableColors(), currentBreak.color]
                  }
                  currentColor={currentBreak.color}
                  onSelect={(color) => setCurrentBreak({ ...currentBreak, color })}
                />
              </div>
            </div>
          </div>
          {/* Modal Footer */}
          <div className="flex justify-end p-4 md:p-5 border-t dark:border-gray-600">
            <button
              type="button"
              onClick={closeModal}
              className="text-gray-900 bg-white border border-gray-200 hover:bg-gray-100 focus:ring-4 focus:ring-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 mr-2"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveBreak}
              className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {/* Header with Breaks title and Add Break button */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xl font-bold text-gray-800">Breaks</h2>
        <button
          type="button"
          onClick={openAddModal}
          className="text-white inline-flex items-center bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-3 py-2.5"
        >
          Add Break
        </button>
      </div>

      {/* Render the modal via a Portal */}
      {isModalOpen && ReactDOM.createPortal(modalContent, document.body)}

      {/* Select All Breaks */}
      <div className="flex items-center mt-4">
        <input
          id="select-all-breaks"
          type="checkbox"
          checked={breaks.length > 0 && breaks.every(bk => bk.selected)}
          onChange={(e) => handleSelectAllBreaks(e.target.checked)}
          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
        />
        <label htmlFor="select-all-breaks" className="ml-2 text-sm font-medium text-gray-700">
          Select All Breaks
        </label>
      </div>

      {/* List of Breaks */}
      <div className="pl-1.5 mt-2 border-t border-gray-200 pt-2 overflow-y-auto">
        {breaks.map((bk, index) => (
          <div key={index} className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <input
                id={`break-${index}`}
                type="checkbox"
                checked={bk.selected}
                onChange={() => toggleBreakSelection(index)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <div className={`w-4 h-4 rounded-full ml-2 ${bk.color}`}></div>
              <label htmlFor={`break-${index}`} className="ml-2 text-sm text-gray-700">
                {bk.name}: {bk.days.join(", ")} {formatTime(bk.start)} - {formatTime(bk.end)}
              </label>
            </div>
            <div className="flex space-x-2">
              <button onClick={() => openEditModal(index)} className="text-blue-600 text-xs">
                Edit
              </button>
              <button
                onClick={() => removeBreak(index)}
                className="text-red-600 text-xs"
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BreaksSection;
